import { FormikErrors, useFormik } from "formik";
import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { useMutation } from "react-query";
import { FaExclamationCircle } from "react-icons/fa";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Label } from "../../components/Label";
import { Input } from "../../components/Input";
import { Button } from "../../components/Button";

export const AddCategories: React.FC = () => {
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const { mutate, error, isLoading, data } = useMutation(async (data: any) => {
    return await axios.post("/categories", data);
  });

  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      names: [
        { language: "ar", name: "" },
        { language: "en", name: "" },
        { language: "ur", name: "" },
      ],
      logo: null,
    },
    validationSchema: Yup.object({
      names: Yup.array()
        .of(
          Yup.object({
            language: Yup.string().required(),
            name: Yup.string().required("Required"),
          })
        )
        .min(1),
    }),
    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        const names = values.names
          .filter((n) => n.name.trim() !== "")
          .map((n) => ({
            name: n.name,
            language: n.language,
          }));

        formData.append("names", JSON.stringify(names));
        if (values.logo) {
          formData.append("categoryLogo", values.logo as File);
        }
        mutate(formData);
      } catch (error) {
        console.log(error);
      }
    },
  });

  useEffect(() => {
    if (!isLoading && data) {
      navigate(-1);
    }
  }, [isLoading, data, navigate]);

  return (
    <div className="flex">
      <form
        className="flex w-8/12 flex-col gap-4 "
        onSubmit={formik.handleSubmit}
      >
        <>
          <h3 className="text-3xl font-medium py-5">Add New Categoy</h3>

          {error && (
            <p className="flex items-center gap-2 rounded-md bg-red-500 p-2 text-white">
              <FaExclamationCircle />
            </p>
          )}

          {formik.values.names.map((item, index) => (
            <div className="w-full" key={index}>
              <Label htmlFor={`names.${index}.name`}>
                Name ({item.language.toUpperCase()})
              </Label>
              <Input
                type="text"
                id={`names.${index}.name`}
                className={`w-full ${
                  item.language === "ur" ? " h-full font-urdu" : ""
                }`}
                {...formik.getFieldProps(`names.${index}.name`)}
                error={
                  formik.touched.names?.[index]?.name
                    ? (
                        formik.errors.names?.[index] as FormikErrors<{
                          language: string;
                          name: string;
                        }>
                      )?.name || ""
                    : ""
                }
              />
            </div>
          ))}

          <div className="w-full">
            <Label htmlFor="logo">Logo</Label>
            <input
              type="file"
              id="logo"
              name="logo"
              accept="image/*"
              onChange={(event) => {
                const file = event.currentTarget.files?.[0];
                if (file) {
                  formik.setFieldValue("logo", file);
                  setLogoPreview(URL.createObjectURL(file));
                }
              }}
              className="w-full border border-gray-300 p-2 rounded"
            />
            {formik.touched.logo && formik.errors.logo && (
              <span className="px-1 text-xs text-red-500">
                {formik.errors.logo}
              </span>
            )}

            {logoPreview && (
              <div className="mt-2">
                <img
                  src={logoPreview}
                  alt="Logo Preview"
                  className="h-20 p-2 object-contain border"
                />
              </div>
            )}
          </div>

          <div className="w-full flex justify-between gap-3">
            <Button green className="w-1/2" type="submit" loading={isLoading}>
              Add
            </Button>
            <Button
              type="button"
              lightRed
              className="w-1/2"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
          </div>
        </>
      </form>
    </div>
  );
};
